import React, { useContext } from 'react'
import { WistiaPlayer } from '@wistia/wistia-player-react'

import { TypeAssetVideo, VideoType } from '../../../lib/types'
import { assertNever } from '../../../lib/utils/assert-never'
import { AnimationVideoPlayer } from '../../ui-lib/video'
import { YoutubeEmbedPopup } from '../../ui-lib/video'
import { SectionContext } from '../../../contexts/section-context'
import { ContentProps } from '../../renderer'
import { updateAssetCDNUrl } from '../utils'

export const VideoAsset: React.FC<ContentProps<TypeAssetVideo>> = (props) => {
  const { className, structure } = props
  const {
    dropShadow,
    videoDescription,
    youtubeId,
    assets,
    videoType,
    poster,
    title,
    thumbnail,
    autoLoop,
    autoPlay,
    internalName,
    controlsLocation,
    wistiaVideo,
  } = structure.fields || {}
  const { indexInPage } = useContext(SectionContext)

  switch (videoType) {
    case VideoType.Animation: {
      const videoSources =
        assets
          ?.filter(({ fields }) => Boolean(fields.file))
          ?.map(({ fields }) => ({ url: fields.file.url, contentType: fields.file.contentType }))
          .filter(({ url, contentType }) => !!url && !!contentType) ?? []
      const hasContentfulVideoAsset = videoSources.length > 0

      if (!hasContentfulVideoAsset) {
        return null
      }

      const internalCDNVideoSources = videoSources.map((videoSource) => ({
        ...videoSource,
        url: updateAssetCDNUrl(videoSource.url),
      }))
      const posterUrl = updateAssetCDNUrl(poster?.fields.file?.url ?? '')
      const shouldLazyLoad = !!(indexInPage && indexInPage > 1)

      return (
        <AnimationVideoPlayer
          className={className}
          videoSources={internalCDNVideoSources}
          controls={false}
          videoDescription={videoDescription}
          lazyLoad={shouldLazyLoad}
          poster={posterUrl}
          loop={autoLoop}
          autoPlay={autoPlay}
          dropShadow={dropShadow}
          controlsLocation={controlsLocation}
          animationInternalName={internalName}
        />
      )
    }
    case VideoType.Youtube: {
      if (!youtubeId) {
        return null
      }
      const thumbnailUrl = thumbnail?.fields.file?.url || ''
      const thumbnailUrlInternalCDN = updateAssetCDNUrl(thumbnailUrl)
      return (
        <YoutubeEmbedPopup
          youtubeId={youtubeId}
          title={title}
          thumbnail={thumbnailUrlInternalCDN}
          videoDescription={videoDescription}
          videoInternalName={internalName}
          dropShadow={dropShadow}
          autoPlay={autoPlay}
        />
      )
    }
    case VideoType.Wistia: {
      // Wistia plugin in Contentful allow selecting multiple videos, but we only need the first one
      const mediaId = wistiaVideo?.items[0]?.hashed_id
      if (!mediaId) {
        return null
      }
      return <WistiaPlayer mediaId={mediaId} />
    }

    default:
      assertNever(videoType)
      return null
  }
}
